import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { FormProvider, UseFormReturn, useWatch } from 'react-hook-form'
import { skipToken } from '@reduxjs/toolkit/query'
import { EditModeProvider } from 'contexts/edit-mode.context'
import { useGetLoadConfirmationDetailsQuery } from 'store/load-confirmation/load-confirmation.api'
import { useGetCarrierCompaniesByUserQuery } from 'store/company/company.api'
import { useUserCompanyLogo } from 'hooks/use-user-company-logo'
import { getUserId } from 'utils/common'
import useSubmitLock from 'hooks/use-submit-lock'
import { TripPayload } from 'store/trips/trips.types'
import { Order } from 'store/orders/orders.types'

import { LoadConfirmationFormValues } from './schema'
import { buildFormFromTripTasks, resolveSourceOrderId } from './utils/build-form-from-trip'
import { CarrierContactDetails } from './utils/carrier-contact'
import { mapFormToDocument } from './utils/map-form-to-document'
import { paginateDocument } from './utils/paginate-document'
import { generateLoadConfirmationPdf } from './utils/generate-pdf'
import { LoadConfirmationDocument } from './template/LoadConfirmationDocument'
import { LoadConfirmationPreview } from './preview/LoadConfirmationPreview'
import { LoadConfirmationFormPanel } from './forms/LoadConfirmationFormPanel'
import { CarrierTripBuilderDialog } from './components/carrier-trip-builder-dialog'
import { appendEquipmentNote } from './utils/equipment-notes'
import { hasTransportTasks } from './utils/has-transport-tasks'
import './template/load-confirmation-template.css'

export type LoadConfirmationModuleProps = {
  open: boolean
  onClose: () => void
  formMethods: UseFormReturn<LoadConfirmationFormValues>
  tripTasks: TripPayload[]
  allOrders: Order[]
  onSaved?: (result?: unknown) => void | Promise<void>
  existingCarrierOrder?: Order | null
  tripCarrier?: {
    carrierName?: string
    carrierCompanyId?: string
  } | null
  prefilledCarrierContact?: CarrierContactDetails | null
  skipCarrierAutoFill?: boolean
  onSave: (
    formData: LoadConfirmationFormValues,
    tripTasks: TripPayload[],
    orders: Order[],
    pdfBlob?: Blob
  ) => Promise<unknown>
  enableCarrierTripBuilder?: boolean
}

export function LoadConfirmationModule({
  open,
  onClose,
  formMethods,
  tripTasks,
  allOrders,
  onSaved,
  existingCarrierOrder,
  tripCarrier,
  prefilledCarrierContact,
  skipCarrierAutoFill = false,
  onSave,
  enableCarrierTripBuilder = false,
}: LoadConfirmationModuleProps) {
  const [submitError, setSubmitError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const { isSubmitting, wrapClick } = useSubmitLock()
  const isBusy = isSaving || isSubmitting
  const [tripBuilderOpen, setTripBuilderOpen] = useState(false)
  const documentRef = useRef<HTMLDivElement>(null)
  const userId = getUserId()
  const { logoUrl } = useUserCompanyLogo(open)

  const sourceOrderId = useMemo(
    () => resolveSourceOrderId(tripTasks, allOrders),
    [tripTasks, allOrders]
  )
  const loadNumber = sourceOrderId ? `LCLC-${sourceOrderId}` : 'LCLC-000000'

  const { data: userDetails } = useGetLoadConfirmationDetailsQuery(
    open && userId ? { userId } : skipToken
  )

  const { data: carrierCompanies = [], isFetching: isFetchingCarriers } =
    useGetCarrierCompaniesByUserQuery(
      open && userId ? { userId } : skipToken
    )

  const carrierSuggestions = useMemo(
    () =>
      carrierCompanies.map((carrier) => ({
        id: carrier.companyId,
        companyId: carrier.companyId,
        companyName: carrier.companyName,
        dispatchName: carrier.dispatchName ?? '',
      })),
    [carrierCompanies]
  )

  const lastResetKeyRef = useRef<string | null>(null)
  const hasInitializedFormRef = useRef(false)

  const transportStopsSelected = hasTransportTasks(tripTasks)

  const syncFormFromTripTasks = useCallback(() => {
    const built = buildFormFromTripTasks(
      tripTasks,
      userDetails,
      existingCarrierOrder,
      tripCarrier,
      prefilledCarrierContact
    )
    const currentNotes = formMethods.getValues('notes') ?? ''
    formMethods.setValue('pickups', built.pickups ?? [], { shouldDirty: true })
    formMethods.setValue('deliveries', built.deliveries ?? [], { shouldDirty: true })
    formMethods.setValue(
      'notes',
      appendEquipmentNote(currentNotes || built.notes || '', tripTasks),
      { shouldDirty: true }
    )
  }, [
    tripTasks,
    userDetails,
    existingCarrierOrder,
    tripCarrier,
    prefilledCarrierContact,
    formMethods,
  ])

  const openTripBuilder = () => {
    setTripBuilderOpen(true)
  }

  const handleTripBuilderConfirm = () => {
    syncFormFromTripTasks()
  }

  useEffect(() => {
    if (!open) {
      lastResetKeyRef.current = null
      hasInitializedFormRef.current = false
      return
    }

    // Full form reset only for LC open / hydration — not when trip tasks change
    // inside the pickup/delivery popup (that was wiping Carrier & Rate).
    const resetKey = [
      existingCarrierOrder?.orderId ?? 'none',
      userDetails?.userId ?? 'none',
      tripCarrier?.carrierCompanyId ?? '',
      tripCarrier?.carrierName ?? '',
      prefilledCarrierContact ? 'prefilled' : 'none',
    ].join('|')

    if (lastResetKeyRef.current === resetKey) return
    lastResetKeyRef.current = resetKey
    setSubmitError('')
    const built = buildFormFromTripTasks(
      tripTasks,
      userDetails,
      existingCarrierOrder,
      tripCarrier,
      prefilledCarrierContact
    )

    if (!hasInitializedFormRef.current) {
      formMethods.reset({
        ...built,
        notes: appendEquipmentNote(built.notes ?? '', tripTasks),
      })
      hasInitializedFormRef.current = true
      return
    }

    // Later hydration (user details / existing LC) — merge without wiping edits.
    const current = formMethods.getValues()
    formMethods.reset({
      ...built,
      myCompanyName: current.myCompanyName || built.myCompanyName,
      myCompanyAddress: current.myCompanyAddress || built.myCompanyAddress,
      issuedBy: current.issuedBy || built.issuedBy,
      myCompanyEmail: current.myCompanyEmail || built.myCompanyEmail,
      myCompanyPhone: current.myCompanyPhone || built.myCompanyPhone,
      myCompanyCell: current.myCompanyCell || built.myCompanyCell,
      myCompanyAfterHoursPhone:
        current.myCompanyAfterHoursPhone || built.myCompanyAfterHoursPhone,
      invoicePodContactName:
        current.invoicePodContactName || built.invoicePodContactName,
      invoicePodEmail: current.invoicePodEmail || built.invoicePodEmail,
      invoicePodPhone: current.invoicePodPhone || built.invoicePodPhone,
      carrierName: current.carrierName || built.carrierName,
      carrierAddress: current.carrierAddress || built.carrierAddress,
      dispatchName: current.dispatchName || built.dispatchName,
      dispatchEmail: current.dispatchEmail || built.dispatchEmail,
      dispatchPhone: current.dispatchPhone || built.dispatchPhone,
      dispatchCell: current.dispatchCell || built.dispatchCell,
      dispatchAfterHours: current.dispatchAfterHours || built.dispatchAfterHours,
      selectedCarrierCompanyId:
        current.selectedCarrierCompanyId || built.selectedCarrierCompanyId,
      currency: current.currency || built.currency,
      baseRate: current.baseRate || built.baseRate,
      detentionCharges: current.detentionCharges || built.detentionCharges,
      layoverCharges: current.layoverCharges || built.layoverCharges,
      fuelSurcharge: current.fuelSurcharge || built.fuelSurcharge,
      otherCharges: current.otherCharges || built.otherCharges,
      notes: appendEquipmentNote(
        current.notes || built.notes || '',
        tripTasks
      ),
      pickups:
        (current.pickups?.length ?? 0) > 0 ? current.pickups : built.pickups,
      deliveries:
        (current.deliveries?.length ?? 0) > 0
          ? current.deliveries
          : built.deliveries,
    })
    // tripTasks intentionally omitted — pickup/delivery popup changes must not
    // full-reset Carrier / Rate. Those sync only via syncFormFromTripTasks on Done.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    userDetails,
    existingCarrierOrder,
    tripCarrier,
    prefilledCarrierContact,
    formMethods,
  ])

  const handleSave = async () => {
    setSubmitError('')
    setIsSaving(true)
    try {
      const formData = formMethods.getValues()
      let pdfBlob: Blob | undefined
      if (documentRef.current) {
        // Allow export DOM to paint logo before canvas capture
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
        })
        pdfBlob = await generateLoadConfirmationPdf(documentRef.current)
      }
      const result = await onSave(formData, tripTasks, allOrders, pdfBlob)
      await onSaved?.(result)
      onClose()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save load confirmation.'
      setSubmitError(message)
    } finally {
      setIsSaving(false)
    }
  }

  /** Hidden full-scale document for PDF export (same template as preview) */
  const formValues = useWatch({ control: formMethods.control }) as LoadConfirmationFormValues
  const exportDoc = useMemo(() => {
    const data = mapFormToDocument(formValues ?? {}, loadNumber)
    return { data, pages: paginateDocument(data) }
  }, [formValues, loadNumber])

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: '96vw' },
          maxWidth: 1500,
          height: { xs: '100%', md: '92vh' },
          maxHeight: 920,
          m: { xs: 0, md: 2 },
          borderRadius: { xs: 0, md: 2 },
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Load Confirmation
        </Typography>
        <IconButton aria-label="close" onClick={onClose} disabled={isBusy}>
          <CloseIcon />
        </IconButton>
      </Box>

      <EditModeProvider initialEditMode forceEditMode suppressAutofill>
        <FormProvider {...formMethods}>
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              height: { xs: 'calc(100vh - 120px)', md: 'calc(92vh - 120px)' },
              overflow: 'hidden',
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            {/* Live preview — 50% */}
            <Box
              sx={{
                width: { xs: '100%', md: '50%' },
                height: { xs: 220, md: '100%' },
                borderRight: { md: 1 },
                borderBottom: { xs: 1, md: 0 },
                borderColor: 'divider',
                minHeight: 0,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <LoadConfirmationPreview loadNumber={loadNumber} logoUrl={logoUrl} />
            </Box>

            {/* Form — 50% */}
            <Box
              sx={{
                width: { xs: '100%', md: '50%' },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <LoadConfirmationFormPanel
                  carrierSuggestions={carrierSuggestions}
                  carrierSuggestionsLoading={isFetchingCarriers}
                  skipCarrierAutoFill={skipCarrierAutoFill}
                  hasTransportStops={transportStopsSelected}
                  onAddPickupDelivery={openTripBuilder}
                  onChangePickupDelivery={openTripBuilder}
                />
              </Box>

              {submitError && (
                <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
                  <Typography color="error" variant="body2">
                    {submitError}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  py: 1,
                  px: 2,
                  flexShrink: 0,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Button
                  variant="contained"
                  color="error"
                  disabled={isBusy}
                  onClick={onClose}
                  sx={{
                    minWidth: 170,
                    fontWeight: 700,
                    textTransform: 'capitalize',
                    borderRadius: '6px',
                  }}
                >
                  Delete
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isBusy}
                  onClick={wrapClick(handleSave)}
                  sx={{
                    minWidth: 170,
                    fontWeight: 700,
                    textTransform: 'capitalize',
                    borderRadius: '6px',
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                      color: 'action.disabled',
                    },
                  }}
                >
                  {isBusy ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Off-screen export root — same HTML template at full A4 scale for PDF */}
          <Box
            aria-hidden
            sx={{
              position: 'fixed',
              left: 0,
              top: 0,
              width: '210mm',
              opacity: 0,
              pointerEvents: 'none',
              zIndex: -1,
            }}
          >
            <div ref={documentRef}>
              <LoadConfirmationDocument
                data={exportDoc.data}
                pages={exportDoc.pages}
                logoUrl={logoUrl}
              />
            </div>
          </Box>
        </FormProvider>
      </EditModeProvider>

      {enableCarrierTripBuilder && (
        <CarrierTripBuilderDialog
          open={tripBuilderOpen}
          onClose={() => setTripBuilderOpen(false)}
          onConfirm={handleTripBuilderConfirm}
        />
      )}
    </Dialog>
  )
}